import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal wraps children and adds a scroll-triggered fade/slide animation when
 * the element enters the viewport. Pure CSS, no external libraries.
 *
 * Props:
 *  - direction: 'up' | 'down' | 'left' | 'right' | 'zoom' | 'fade'
 *  - delay: ms before animation starts (per element)
 *  - threshold: 0..1 IntersectionObserver threshold
 *  - as: HTML tag name to render (default 'div')
 */
const Reveal = ({
  children,
  direction = "up",
  delay = 0,
  threshold = 0.15,
  as: Tag = "div",
  className = "",
  once = true,
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) obs.unobserve(el);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -50px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, once]);

  return (
    <Tag
      ref={ref}
      className={`reveal reveal-${direction} ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
